/**
 * driving_log_sink — 지금 켜져 있는 주행 로거를 가리키는 모듈 싱글턴.
 *
 * 로깅 지점(MQTT 송수신 탭, 키보드 탭)은 콕핏 컴포넌트 트리 바깥이거나 아주 깊은 곳에
 * 있다. 로거 인스턴스를 거기까지 prop 으로 내리는 대신, 이 모듈 하나를 보게 한다.
 *
 * **핫 패스 비용**: 로깅이 꺼져 있으면 `activeLogger == null` 불리언 한 번으로 끝난다.
 * MQTT 수신 경로는 초당 수백 번 불리므로, 켜기 전에는 디코드는 물론 토픽 조회도 하지
 * 않아야 한다.
 */
import {
  DRIVING_LOG_RX_ITEM_BY_TOPIC,
  DRIVING_LOG_TX_ITEM_BY_TOPIC,
  isDrivingLogItemEnabled,
} from "./driving_log_options";
import type { DrivingLogDirection } from "./driving_logger";

/** 로거가 제공해야 하는 최소 계약 — 탭이 DrivingLogger 구현에 묶이지 않게 한다. */
export interface DrivingLogSink {
  active: boolean;
  logTopic(
    direction: DrivingLogDirection,
    itemId: string,
    data: unknown,
    topic?: string,
  ): void;
  logDebug(itemId: string, data: unknown): void;
}

let activeLogger: DrivingLogSink | null = null;

export function setActiveDrivingLogger(logger: DrivingLogSink | null): void {
  activeLogger = logger;
}

export function getActiveDrivingLogger(): DrivingLogSink | null {
  return activeLogger;
}

function itemIdForTopic(
  direction: DrivingLogDirection,
  topic: string,
): string | undefined {
  const index =
    direction === "rx" ? DRIVING_LOG_RX_ITEM_BY_TOPIC : DRIVING_LOG_TX_ITEM_BY_TOPIC;
  return index.get(topic);
}

/**
 * 이 토픽을 지금 기록해야 하는가 — 디코드 **전에** 물어보기 위한 문지기.
 * 디코드는 protobuf 파싱이라 공짜가 아니므로 반드시 이 검사를 먼저 통과해야 한다.
 */
export function isDrivingTopicLogEnabled(
  direction: DrivingLogDirection,
  topic: string,
): boolean {
  if (!activeLogger || !activeLogger.active) {
    return false;
  }
  const itemId = itemIdForTopic(direction, topic);
  return itemId != null && isDrivingLogItemEnabled(itemId);
}

/** 디코드된 토픽 한 건을 기록한다. 꺼져 있으면 아무 일도 하지 않는다. */
export function logDrivingTopic(
  direction: DrivingLogDirection,
  topic: string,
  data: unknown,
): void {
  const logger = activeLogger;
  if (!logger || !logger.active) {
    return;
  }
  const itemId = itemIdForTopic(direction, topic);
  if (!itemId) {
    return;
  }
  logger.logTopic(direction, itemId, data, topic);
}

export function isDrivingDebugEnabled(itemId: string): boolean {
  return (
    activeLogger != null && activeLogger.active && isDrivingLogItemEnabled(itemId)
  );
}

export function logDrivingDebug(itemId: string, data: unknown): void {
  const logger = activeLogger;
  if (!logger || !logger.active) {
    return;
  }
  logger.logDebug(itemId, data);
}

// 항목별 마지막 기록 시각 — 휠/게임패드 입력처럼 60Hz 로 들어오는 신호를 솎아낸다.
const lastLoggedAtMs = new Map<string, number>();

/**
 * 고빈도 항목용 — `minIntervalMs` 보다 촘촘히 들어오는 호출은 버린다.
 * (휠/게임패드는 60Hz 폴링이라 그대로 남기면 파일의 대부분이 같은 값이 된다.)
 */
export function logDrivingDebugThrottled(
  itemId: string,
  data: unknown,
  minIntervalMs: number,
  now: number = Date.now(),
): void {
  if (!isDrivingDebugEnabled(itemId)) {
    return;
  }
  const last = lastLoggedAtMs.get(itemId);
  if (last != null && now - last < minIntervalMs) {
    return;
  }
  lastLoggedAtMs.set(itemId, now);
  logDrivingDebug(itemId, data);
}

/**
 * 키보드 탭 설치 — 모든 키의 keydown/keyup 을 남긴다.
 *
 * capture + passive 로 붙는 이유: 기존 단축키 핸들러보다 먼저 보되(capture) 절대
 * 가로채거나 기본 동작을 막지 않기 위해서다(passive 는 preventDefault 를 금지하므로
 * 실수로도 조작을 삼킬 수 없다).
 */
export function installDrivingLogKeyboardTap(
  target: Window = window,
): () => void {
  const handle = (event: KeyboardEvent) => {
    if (!isDrivingDebugEnabled("debug.keyboard")) {
      return;
    }
    const element = event.target as { tagName?: string } | null;
    logDrivingDebug("debug.keyboard", {
      phase: event.type,
      key: event.key,
      code: event.code,
      repeat: event.repeat,
      alt: event.altKey,
      ctrl: event.ctrlKey,
      shift: event.shiftKey,
      meta: event.metaKey,
      target: element?.tagName ?? null,
    });
  };
  const options = { capture: true, passive: true } as AddEventListenerOptions;
  target.addEventListener("keydown", handle as EventListener, options);
  target.addEventListener("keyup", handle as EventListener, options);
  return () => {
    target.removeEventListener("keydown", handle as EventListener, options);
    target.removeEventListener("keyup", handle as EventListener, options);
  };
}

/** 테스트 전용 — 스로틀 상태 초기화. */
export function resetDrivingLogSinkForTests(): void {
  activeLogger = null;
  lastLoggedAtMs.clear();
}
